import React, { useContext, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import debounce from 'lodash/debounce';
import { registerGrid, unregisterGrid, loadGridData, clearRefresh } from '../state/gridActions';
import { getQueryParams, doesNeedRefresh } from '../state/gridSelectors';

const GridContext = React.createContext({ name: '' });

export const useGridContext = () => {
    const context = useContext(GridContext);
    return context.name;
};

const updateGridData = (name, loadData, onDataReceived) => async (dispatch, getState) => {
    const state = getState();
    let response = await dispatch(loadData(getQueryParams(state, name)));
    dispatch(onDataReceived(response));
    dispatch(loadGridData(name, response));
};

const BaseGridProvider = ({ name, primaryKey, loadData, onDataReceived, manual, retainOnUnmount, children }) => {
    const dispatch = useDispatch();
    const needsUpdate = useSelector(state => doesNeedRefresh(state, name));

    const getNewDataRef = useRef();
    getNewDataRef.current = () => {
        dispatch(updateGridData(name, loadData, onDataReceived));
    };

    const debouncedGetNewData = useCallback(
        debounce(() => getNewDataRef.current(), 0),
        []
    );

    useEffect(() => {
        dispatch(registerGrid(name, primaryKey));
        if (!manual) {
            debouncedGetNewData();
        }
        return () => {
            if (!manual && !retainOnUnmount) {
                dispatch(unregisterGrid(name));
            }
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!manual && needsUpdate) {
            dispatch(clearRefresh(name));
            // Do not debounce refresh: debounce(0) can drop/coalesce updates and block search/filter refetches.
            getNewDataRef.current();
        }
    }, [needsUpdate]); // eslint-disable-line react-hooks/exhaustive-deps

    return <GridContext.Provider value={{ name }}>{children}</GridContext.Provider>;
};

export const GridProvider = BaseGridProvider;

// Deprecated: use useGridContext() hook instead
export const consumer = Component => ({ children, ...props }) => {
    return (
        <GridContext.Consumer>
            {context => (
                <Component gridName={context.name} {...props}>
                    {children}
                </Component>
            )}
        </GridContext.Consumer>
    );
};
